import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Download, FileText, ExternalLink, Sparkles } from "lucide-react";
import articles from "@/data/blogArticles";

// Convert one essay into clean plain text (intro + body sections + closing)
const essayToText = (slug) => {
  const a = articles.find((x) => x.slug === slug);
  if (!a) return "";
  const parts = [
    `# ${a.title}`,
    "",
    `By Temple Franklin — Mind Body Spirit HYGIENE™ Tools`,
    "",
    a.content.intro,
    "",
    ...a.content.body.flatMap((s) => [`## ${s.heading}`, "", s.text || "", ""]),
    a.content.closing,
  ];
  return parts.join("\n").trim();
};

const CopyBtn = ({ value, label = "Copy", testId, big }) => {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };
  return (
    <button
      onClick={handle}
      className={`inline-flex items-center gap-2 ${
        big
          ? "bg-[#25a1c2] hover:bg-[#068fa2] text-white text-sm font-bold rounded-full px-6 py-3"
          : "bg-[#25a1c2] hover:bg-[#068fa2] text-white text-xs font-bold rounded-full px-4 py-2"
      } transition-all`}
      data-testid={testId}
    >
      {copied ? <Check className={big ? "w-4 h-4" : "w-3.5 h-3.5"} /> : <Copy className={big ? "w-4 h-4" : "w-3.5 h-3.5"} />}
      {copied ? "Copied!" : label}
    </button>
  );
};

export default function VoicePack() {
  const [voiceMd, setVoiceMd] = useState("Loading…");

  useEffect(() => {
    document.title = "Voice Pack for Claude Cowork | Temple Franklin";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);

    fetch("/voice-pack/temple-voice-and-story.md")
      .then((r) => r.text())
      .then(setVoiceMd)
      .catch(() => setVoiceMd("Could not load voice file. Try refreshing."));

    return () => document.head.removeChild(meta);
  }, []);

  const tapIn = essayToText("the-tap-in");
  const toxic = essayToText("toxic-positivity");
  const messy = essayToText("messy-middle-code-of-conduct");

  const everything = [
    "═══════════════════════════════════════════",
    "  TEMPLE FRANKLIN — VOICE PACK",
    "  4 sources for Claude Cowork voice training",
    "═══════════════════════════════════════════",
    "",
    "──── SOURCE 1 OF 4: TEMPLE'S VOICE & STORY ────",
    "",
    voiceMd,
    "",
    "──── SOURCE 2 OF 4: AUDIO ESSAY — THE TAP-IN ────",
    "",
    tapIn,
    "",
    "──── SOURCE 3 OF 4: AUDIO ESSAY — TOXIC POSITIVITY ────",
    "",
    toxic,
    "",
    "──── SOURCE 4 OF 4: AUDIO ESSAY — THE MESSY MIDDLE ────",
    "",
    messy,
    "",
    "═══════════════════════════════════════════",
    "  END OF VOICE PACK",
    "═══════════════════════════════════════════",
  ].join("\n");

  const SOURCES = [
    {
      n: 1,
      title: "Temple's Voice & Story",
      sublabel: "504-line backstory, philosophy, lineage, brand voice",
      filename: "temple-voice-and-story.md",
      content: voiceMd,
      downloadUrl: "/voice-pack/temple-voice-and-story.md",
      blogUrl: null,
      testId: "source-1",
    },
    {
      n: 2,
      title: "Audio Essay — The Tap-In",
      sublabel: "The HYGIENE that reclaimed my life · 9 min",
      filename: "the-tap-in.txt",
      content: tapIn,
      downloadUrl: null,
      blogUrl: "/blog/the-tap-in",
      testId: "source-2",
    },
    {
      n: 3,
      title: "Audio Essay — Toxic Positivity",
      sublabel: "The quiet violence of toxic positivity · 9 min",
      filename: "toxic-positivity.txt",
      content: toxic,
      downloadUrl: null,
      blogUrl: "/blog/toxic-positivity",
      testId: "source-3",
    },
    {
      n: 4,
      title: "Audio Essay — The Messy Middle",
      sublabel: "Personal Code of Conduct · 12 min",
      filename: "messy-middle-code-of-conduct.txt",
      content: messy,
      downloadUrl: null,
      blogUrl: "/blog/messy-middle-code-of-conduct",
      testId: "source-4",
    },
  ];

  // Helper: download as .txt file
  const downloadTxt = (filename, text) => {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-[#efeae7] min-h-screen pb-16" data-testid="voice-pack-page">
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/social"
            className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Publish Toolkit
          </Link>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#25a1c2] bg-[#F0F5F4] px-2.5 py-1 rounded-full">
            Voice Pack · Private
          </span>
        </div>
      </header>

      <div className="pt-28 px-6">
        <div className="max-w-5xl mx-auto">
          {/* HERO */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#25a1c2] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
              <Sparkles className="w-3 h-3" /> For Claude Cowork
            </div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#042F2E] mb-4">
              Your Voice Pack
            </h1>
            <p className="text-[#475569] max-w-2xl mx-auto">
              All 4 voice samples in one place. Copy each one into Claude Cowork when it asks for writing samples — or copy ALL FOUR at once with the big button below.
            </p>
          </div>

          {/* THE BIG BUTTON */}
          <div className="bg-gradient-to-br from-[#042F2E] via-[#25a1c2] to-[#068fa2] rounded-2xl p-8 mb-12 text-center text-white" data-testid="copy-all-card">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold mb-2">
              The Easy Way
            </p>
            <h2 className="font-serif text-2xl md:text-3xl mb-3">
              Copy All 4 Sources at Once
            </h2>
            <p className="text-white/80 text-sm mb-6 max-w-md mx-auto">
              Paste this into Claude Cowork in a single message. Tell Claude: <em>"These are 4 voice samples. Use them to build my write-content skill."</em>
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <CopyBtn
                value={everything}
                label="Copy All 4 Sources"
                testId="copy-all-btn"
                big
              />
              <button
                onClick={() => downloadTxt("temple-voice-pack.txt", everything)}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-bold rounded-full px-6 py-3 transition-all border border-white/20"
                data-testid="download-all-btn"
              >
                <Download className="w-4 h-4" /> Download as .txt
              </button>
            </div>
            <p className="text-[#5EEAD4]/70 text-xs italic mt-4">
              ~50,000 characters. Claude can handle it.
            </p>
          </div>

          {/* INSTRUCTIONS */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-10" data-testid="instructions-card">
            <p className="font-bold text-amber-900 mb-3">📋 Step-by-step in Claude Cowork:</p>
            <ol className="space-y-2 text-sm text-amber-900 list-decimal list-inside">
              <li>Open Claude → start a new <strong>Project</strong> (or create the <code className="bg-amber-100 px-1 rounded">/write-content</code> skill)</li>
              <li>Paste the prompt: <em>"Create a write-content skill that writes social posts in my brand voice. Interview me until 95% confident."</em></li>
              <li>When Claude asks for samples → click <strong>"Copy All 4 Sources"</strong> above → paste into the chat</li>
              <li>Answer Claude's interview questions honestly (~90 min)</li>
              <li>Save the skill. Done forever. 💛</li>
            </ol>
          </div>

          {/* INDIVIDUAL SOURCES */}
          <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-4 text-center">
            OR · Copy Each Source Individually
          </p>

          <div className="space-y-5" data-testid="sources-list">
            {SOURCES.map((src) => (
              <div
                key={src.n}
                className="bg-white rounded-2xl border border-[#CCFBF1] p-6 shadow-sm"
                data-testid={src.testId}
              >
                <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#F0F5F4] rounded-full flex items-center justify-center">
                      <span className="font-serif text-xl text-[#25a1c2] font-bold">{src.n}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif text-lg text-[#042F2E] leading-tight">{src.title}</h3>
                      <p className="text-[#475569] text-xs mt-0.5">{src.sublabel}</p>
                      <p className="text-[#25a1c2] text-[10px] font-mono mt-1">
                        {src.content ? `${src.content.length.toLocaleString()} chars` : "loading…"}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <CopyBtn
                      value={src.content}
                      label="Copy"
                      testId={`copy-${src.testId}`}
                    />
                    <button
                      onClick={() => downloadTxt(src.filename, src.content)}
                      className="inline-flex items-center gap-1.5 bg-[#F0F5F4] hover:bg-[#CCFBF1] text-[#25a1c2] text-xs font-bold rounded-full px-4 py-2 transition-all"
                      data-testid={`download-${src.testId}`}
                    >
                      <Download className="w-3.5 h-3.5" /> .txt
                    </button>
                    {src.blogUrl && (
                      <Link
                        to={src.blogUrl}
                        className="inline-flex items-center gap-1.5 text-[#25a1c2] hover:text-[#042F2E] text-xs font-bold px-3 py-2 transition-all"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> View
                      </Link>
                    )}
                    {src.downloadUrl && (
                      <a
                        href={src.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#25a1c2] hover:text-[#042F2E] text-xs font-bold px-3 py-2 transition-all"
                      >
                        <FileText className="w-3.5 h-3.5" /> Open .md
                      </a>
                    )}
                  </div>
                </div>

                {/* Preview (first 300 chars) */}
                <div className="bg-[#efeae7] border border-[#CCFBF1] rounded-xl p-4 mt-3">
                  <p className="text-[#475569] text-xs leading-relaxed line-clamp-3 font-mono">
                    {src.content ? src.content.slice(0, 300) + "…" : "Loading content…"}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* DIRECT URL */}
          <div className="mt-12 bg-white rounded-2xl border border-[#CCFBF1] p-6 text-center">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-2">
              Direct URL · Bookmark This
            </p>
            <p className="font-mono text-sm text-[#042F2E] bg-[#F0F5F4] inline-block px-4 py-2 rounded-full">
              mindbodyspirithygienetools.com/voice-pack
            </p>
          </div>

          <p className="text-center text-[10px] text-[#475569] italic mt-6">
            Private page · noindex · only people with the URL can access this
          </p>
        </div>
      </div>
    </div>
  );
}
